import React, { useEffect, useMemo, useState } from "react";

import { LazyQueryExecFunction } from "@apollo/client";
import { AnyFilterExpressionInput, Attribute, BooleanOp, ByProjectIdReqInput, Exact, FilterOp, MdmDeviceDto, MdmObjectDto, SortDirection, SortInput, useUpdateUserSettingsMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import useProjectContext from "../hooks/useProjectContext";
import { TableOptions, TableColumn, MdmTableName, MdmContainerName } from "../utils/types";
import { errorToaster, successToaster } from "../utils/utils";
import LazyTable from "./LazyTable";
import TableColumnSelection from "./TableColumnSelection";
import { initialFilter } from "../utils/hooks/useTableFilter";

type CustomFieldType = Record<
  string,
  {
    value: string | null | undefined;
    id: string | null | undefined;
    title: string | undefined;
  }
>;

type ObjectAndDeviceTableWrapperProps<T, N> = {
  data: T[];
  tableName: MdmTableName;
  containerName: MdmContainerName;
  columns: TableColumn<T>[];
  loading: boolean;
  setTableOptions: React.Dispatch<React.SetStateAction<TableOptions>>;
  tableOptions: TableOptions;
  totalItems: number;
  getQueryData: LazyQueryExecFunction<N, Exact<{ input: ByProjectIdReqInput }>>;
};

const ObjectAndDeviceTableWrapper = <T extends NoInfer<MdmDeviceDto | MdmObjectDto>, N>({ 
  data,
  tableName,
  containerName,
  columns,
  loading,
  setTableOptions,
  tableOptions,
  totalItems,
  getQueryData,
 }: ObjectAndDeviceTableWrapperProps<T, N>) => {
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const { user, userSettings, project, projectId } = useProjectContext();
  const navigate = useNavigate();

  const [updateUserSettings, { loading: updateLoading }] = useUpdateUserSettingsMutation({
    onCompleted: () => successToaster("Columns saved successfully"),
    onError: errorToaster,
  });

  let columnList: string[] = [];
  let customCol: Attribute[] = [];

  if (tableName === "Devices") {
    columnList = (userSettings?.userSettings.deviceTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? []),
    ] as Attribute[];
  } else if (tableName === "Objects") {
    columnList = (userSettings?.userSettings.objectTable as string[]) ?? [];
    customCol = [
      ...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? []),
    ] as Attribute[];
  }

  useEffect(() => {
    if (userSettings && columnList.length > 0) {
      setCustomColumns(columnList);
    } else {
      setCustomColumns(columns.map(col => col.header));
    }
  }, [userSettings]);

  const mapCol = useMemo(
    () =>
      [
        ...columns,
        ...(customCol?.map(col => ({
          field: `${col?.id}.value`,
          header: col?.title ?? "",
          sortable: true,
          columnKey: col?.id,
        })) ?? []),
      ] as TableColumn<T>[],
    [columns]
  );

  const formatObjectFields = data.reduce((acc, obj) => {
    const objFields = obj.customFields?.reduce((accObj, field) => {
      const newObj = {
        value: field?.value?.value,
        id: field?.key,
        title: customCol.find(col => col?.id === field?.key)?.title,
      };
      return {
        ...accObj,
        [field?.key!]: newObj,
      };
    }, {} as CustomFieldType);

    acc.push({
      ...obj,
      ...objFields,
    });

    return acc;
  }, [] as Partial<T>[]);

  const filteredColumns = useMemo(
    () => mapCol.filter(col => customColumns.includes(col.header)),
    [customColumns, mapCol]
  );

  const handleColumnSave = () => {
    if (tableName === "Devices") {
      updateUserSettings({
        variables: {
          userSettings: {
            id: user?.externalId!,
            deviceTable: customColumns,
          },
        },
      });
      return;
    }
    updateUserSettings({
      variables: {
        userSettings: {
          id: user?.externalId!,
          objectTable: customColumns,
        },
      },
    });
  };

  const fetchData = async (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
    filter: AnyFilterExpressionInput | null = null,
  ) => {
    const resp = await getQueryData({
      variables: {
        input: {
          projectId,
          take: take ?? tableOptions.rows,
          skip: skip ?? tableOptions.first,
          sort: sort ?? [{
            property: tableOptions.sortField,
            direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? tableOptions.andFilters,
          filter: filter ?? tableOptions.filter,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });

    return resp.data
  };

  const handleSearch = (value: string) => {
    const searchFilter: AnyFilterExpressionInput | null = {
      op: BooleanOp.Or,
      left: {
        op: BooleanOp.Or,
        left: {
          value,
          cond: FilterOp.Contains,
          property:
            tableName === "Devices"
              ? "objectRelation.mdmObject.address.fullAddress"
              : "address.fullAddress",
        },
        right: {
          value,
          cond: FilterOp.Contains,
          property: tableName === "Devices" ? "config.title" : "title",
        },
      },
      right: {
        value,
        cond: FilterOp.Contains,
        property: "identifier",
      },
    };
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchFilter : null,
      first: 0,
      search: value,
    }));
    fetchData(null, 0, null, null, true, searchFilter);
  };

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: "identifier",
      reqSortField: "identifier",
      sortOrder: 1,
    }));
    fetchData(
      tableOptions.rows,
      0,
      [{ direction: SortDirection.Asc, property: "identifier" }],
      [],
      false,
      { cond: FilterOp.Contains, property: "identifier", value: "" }
    );
  };
  
  return (
    <LazyTable
      columns={filteredColumns}
      data={formatObjectFields}
      tableName={tableName}
      containerName={containerName}
      loading={loading}
      setTableOptions={setTableOptions}
      tableOptions={tableOptions}
      totalItems={totalItems}
      fetchData={fetchData}
      resetFilters={resetFilters}
      onSearch={handleSearch}
      buttons={
        <>
          <TableColumnSelection
            customColumns={customColumns}
            setCustomColumns={setCustomColumns}
            handleColumnSave={handleColumnSave}
            mapCol={mapCol}
            loading={updateLoading}
          />
          <Button
            type="button"
            icon="pi pi-plus"
            label={tableName === "Devices" ? "Add Device" : "Add Object"}
            onClick={() => navigate(tableName === "Devices" ? "new" : "objects/new")}
          />
        </>
      }
    />
  );
}

export default ObjectAndDeviceTableWrapper;